import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, User, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/auth'
import { useNotifications } from '@/hooks/useNotifications'
import { notificationIcons, formatNotificationTime, getNotificationColors, hasActions } from '@/utils/notifications'
import { ScoreConfirmDialog } from '@/components/league'
import type { Notification, NotificationAction } from '@/services/notificationService'
import type { ScoreSet } from '@/services/matchService'

type FilterTab = 'all' | 'personal' | 'group'

type MatchFormat = 'bestOf1' | 'bestOf3' | 'bestOf5'

interface PlayerInfo {
  userId: string
  displayName: string
  avatar?: string
}

interface ScoreDialogState {
  open: boolean
  mode: 'accept' | 'dispute'
  matchId: string
  bracketId: string
  submitterName?: string
  score?: string
  sets?: ScoreSet[]
  matchFormat?: MatchFormat
  player1?: PlayerInfo
  player2?: PlayerInfo
  notificationId: string
}

export function NotificationBell() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { 
    notifications, 
    personalNotifications,
    groupNotifications,
    unreadCount, 
    isLoading, 
    error, 
    markAsRead, 
    markAllAsRead,
    executeAction,
    refetch,
  } = useNotifications()
  const [filter, setFilter] = useState<FilterTab>('all')
  const [scoreDialog, setScoreDialog] = useState<ScoreDialogState>({
    open: false,
    mode: 'accept',
    matchId: '',
    bracketId: '',
    notificationId: '',
    sets: undefined,
    matchFormat: undefined,
    player1: undefined,
    player2: undefined,
  })

  if (!isAuthenticated) {
    return null
  }

  if (isLoading) {
    return <Skeleton className="h-9 w-9 rounded-md" />
  }

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read when clicked
    if (!notification.read) {
      await markAsRead(notification.id, notification.scope, notification.groupId)
    }

    // If notification has a primary navigation action, navigate there
    const primaryAction = notification.actions?.find(a => a.primary && a.url)
    if (primaryAction?.url) {
      navigate(primaryAction.url)
    }
  }

  const handleActionClick = async (
    e: React.MouseEvent, 
    notification: Notification, 
    action: NotificationAction
  ) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Handle score confirmation actions with a dialog
    if (action.type === 'accept_score' || action.type === 'dispute_score') {
      // Extract score from notification message (fallback)
      const scoreMatch = notification.message.match(/\(([^)]+)\)/)
      const score = scoreMatch ? scoreMatch[1] : undefined

      // Extract metadata from notification
      const metadata = notification.metadata as {
        sets?: ScoreSet[]
        matchFormat?: MatchFormat
        player1?: PlayerInfo
        player2?: PlayerInfo
      } | undefined
      
      setScoreDialog({
        open: true,
        mode: action.type === 'accept_score' ? 'accept' : 'dispute',
        matchId: notification.matchId || '',
        bracketId: notification.bracketId || '',
        submitterName: notification.fromUserName,
        score,
        sets: metadata?.sets,
        matchFormat: metadata?.matchFormat,
        player1: metadata?.player1,
        player2: metadata?.player2,
        notificationId: notification.id,
      })
      return
    }
    
    const result = await executeAction(notification.id, action.type)
    
    // Handle navigation actions
    if (result?.url) {
      navigate(result.url)
    }
  }

  const handleScoreDialogSuccess = () => {
    // Refetch notifications to get updated state (actions removed)
    refetch()
  }

  // Calculate unread counts
  const personalUnread = personalNotifications.filter(n => !n.read).length
  const groupUnread = groupNotifications.filter(n => !n.read).length

  // Get filtered notifications
  const filteredNotifications = filter === 'all' 
    ? notifications 
    : filter === 'personal' 
      ? personalNotifications 
      : groupNotifications

  // Show only the 5 most recent notifications in dropdown
  const recentNotifications = filteredNotifications.slice(0, 5)

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-[11px] font-medium text-primary-foreground flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end" forceMount>
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-auto py-0 px-2 text-xs font-normal text-primary hover:text-primary/80"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                markAllAsRead()
              }}
            >
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        
        {/* Filter tabs */}
        <div className="flex gap-1 px-2 pb-2">
          <Button
            variant={filter === 'all' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2 text-xs flex-1"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setFilter('all')
            }}
          >
            All
            {unreadCount > 0 && (
              <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px]">
                {unreadCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={filter === 'personal' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2 text-xs flex-1"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setFilter('personal')
            }}
          >
            <User className="h-3 w-3 mr-1" />
            Me
            {personalUnread > 0 && (
              <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px]">
                {personalUnread}
              </Badge>
            )}
          </Button>
          <Button
            variant={filter === 'group' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2 text-xs flex-1"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setFilter('group')
            }}
          >
            <Users className="h-3 w-3 mr-1" />
            Groups
            {groupUnread > 0 && (
              <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px]">
                {groupUnread}
              </Badge>
            )}
          </Button>
        </div>
        
        <DropdownMenuSeparator />
        <div className="max-h-[300px] overflow-y-auto">
          {error ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Failed to load notifications
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {filter === 'all' 
                ? 'No notifications' 
                : filter === 'personal'
                  ? 'No personal notifications'
                  : 'No group notifications'
              }
            </div>
          ) : (
            recentNotifications.map((notification) => {
              const Icon = notificationIcons[notification.type] || Bell
              const colors = getNotificationColors(notification.type, notification.read)
              const showActions = hasActions(notification)
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={`mt-0.5 rounded-full p-2 ${colors.bg}`}>
                    <Icon className={`h-4 w-4 ${colors.text}`} />
                  </div>
                  <div className="flex-1 space-y-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className={`text-sm leading-none ${notification.read ? 'text-muted-foreground' : 'font-medium'}`}>
                        {notification.title}
                      </p>
                      {notification.scope === 'group' && (
                        <span className="inline-flex items-center text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          <Users className="h-3 w-3 mr-0.5" />
                          {notification.groupName || 'Group'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notification.message}
                    </p>
                    
                    {/* Action buttons */}
                    {showActions && (
                      <div className="flex gap-1.5 mt-2">
                        {notification.actions?.map((action, idx) => (
                          <Button
                            key={idx}
                            variant={action.primary ? 'default' : 'outline'}
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={(e) => handleActionClick(e, notification, action)}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground/70">
                      {formatNotificationTime(notification.createdAt)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="h-2 w-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  )}
                </DropdownMenuItem>
              )
            })
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="justify-center text-sm text-primary cursor-pointer">
          <Link to="/notifications">View all notifications</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Score Confirmation Dialog */}
    <ScoreConfirmDialog
      open={scoreDialog.open}
      onOpenChange={(open) => setScoreDialog(prev => ({ ...prev, open }))}
      mode={scoreDialog.mode}
      matchId={scoreDialog.matchId}
      bracketId={scoreDialog.bracketId}
      submitterName={scoreDialog.submitterName}
      score={scoreDialog.score}
      sets={scoreDialog.sets}
      matchFormat={scoreDialog.matchFormat}
      player1={scoreDialog.player1}
      player2={scoreDialog.player2}
      onSuccess={handleScoreDialogSuccess}
    />
    </>
  )
}
